import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CompleteIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { TaskStatistics } from '../../types/todoTypes';
import { getTaskStatistics } from '../../services/todoService';

interface TodoHeaderProps {
  onCreateClick: () => void;
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const TodoHeader: React.FC<TodoHeaderProps> = ({ onCreateClick, refreshTrigger }) => {
  const [stats, setStats] = useState<TaskStatistics>({
    total: 0,
    pending: 0,
    completed: 0,
    createdToday: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, [refreshTrigger]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const fetchedStats = await getTaskStatistics();
      setStats(fetchedStats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with Create button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          To-Do Tasks
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onCreateClick}
          sx={{ px: 3 }}
        >
          Create Task
        </Button>
      </Box>

      {/* Statistics cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Tasks */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '12px',
                bgcolor: 'primary.main',
                color: 'white',
              }}
            >
              <AssignmentIcon fontSize="large" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Total Tasks
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {loading ? <CircularProgress size={24} /> : stats.total}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Pending Tasks */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '12px',
                bgcolor: 'warning.main',
                color: 'white',
              }}
            >
              <PendingIcon fontSize="large" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Pending Tasks
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {loading ? <CircularProgress size={24} /> : stats.pending}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Completed Tasks */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '12px',
                bgcolor: 'success.main',
                color: 'white',
              }}
            >
              <CompleteIcon fontSize="large" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Completed Tasks
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {loading ? <CircularProgress size={24} /> : stats.completed}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Created Today */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '12px',
                bgcolor: 'info.main',
                color: 'white',
              }}
            >
              <TodayIcon fontSize="large" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Created Today
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {loading ? <CircularProgress size={24} /> : stats.createdToday}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TodoHeader;
